import * as React from 'react'

import {
  IconChevronRight,
  IconFileDescription,
  IconFileTextSpark,
  IconMail,
  IconSearchOff,
  IconShieldLock,
} from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import { useSearch } from '@/api/v1/platform/search'
import { Avatar } from '@/components/sui/avatar'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/sui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/sui/dialog'
import { Kbd } from '@/components/sui/kbd'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useIsMobile } from '@/hooks/use-mobile'
import { useNavigateWithSearchParams } from '@/hooks/use-navigate-with-search-params'
import { trackSearchOpened } from '@/lib/analytics'
import { formatShortcut } from '@/lib/platform'

import { useFilteredPages, useSearchPermissions } from './hooks'
import locales from './locales'
import { SearchDialogSkeleton } from './search-dialog.skeleton'

interface SearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const { t } = useComponentTranslation(locales)
  const navigate = useNavigate()
  const navigateWithSearchParams = useNavigateWithSearchParams()
  const { isAdmin } = useSearchPermissions()
  const isMobile = useIsMobile()

  const [query, setQuery] = React.useState('')
  const debouncedQuery = useDebouncedValue(query, 300)
  const { filteredPages, filteredActions, shortcuts } = useFilteredPages(
    query,
    t,
  )

  const searchParams =
    debouncedQuery.length >= 2
      ? {
          key: debouncedQuery,
          includeVaults: true,
          includeDocuments: true,
          includeEnvelopes: true,
          includeTemplates: true,
          includeContacts: true,
          includeSigners: true,
          includeUserAccounts: isAdmin,
        }
      : null

  const { data: searchResults, isLoading: isSearching } =
    useSearch(searchParams)

  // Focus the command input when dialog opens to ensure arrow key navigation works
  React.useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        const input = document.querySelector<HTMLInputElement>('[cmdk-input]')
        input?.focus()
      })
    }
  }, [open])

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Toggle dialog with Cmd/Ctrl+K
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        if (!open) trackSearchOpened('shortcut')
        onOpenChange(!open)
        return
      }

      // Cmd/Ctrl+digit shortcuts for the root pages (only when dialog is open)
      if (open && (e.metaKey || e.ctrlKey) && /^\d$/.test(e.key)) {
        const path = shortcuts[e.key]
        if (path) {
          e.preventDefault()
          onOpenChange(false)
          navigate({ to: path })
        }
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [open, onOpenChange, navigate, shortcuts])

  const handleNavigate = (path: string) => {
    onOpenChange(false)
    navigateWithSearchParams(path)
  }

  const hasApiResults =
    (searchResults?.envelopes?.length ?? 0) > 0 ||
    (searchResults?.documents?.length ?? 0) > 0 ||
    (searchResults?.templates?.length ?? 0) > 0 ||
    (searchResults?.contacts?.length ?? 0) > 0 ||
    (searchResults?.signers?.length ?? 0) > 0 ||
    (searchResults?.vaults?.length ?? 0) > 0 ||
    (searchResults?.userAccounts?.length ?? 0) > 0

  const isPendingSearch = query !== debouncedQuery && query.length >= 2

  const hasAnyResults =
    filteredPages.length > 0 || filteredActions.length > 0 || hasApiResults

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) setQuery('')
      }}
    >
      <DialogContent
        className="overflow-hidden rounded-xl! p-0"
        showCloseButton={false}
        onKeyDown={(e: React.KeyboardEvent) => {
          // Prevent base-ui Dialog from calling stopPropagation on arrow keys
          // which interferes with cmdk's keyboard navigation
          if (
            e.key === 'ArrowUp' ||
            e.key === 'ArrowDown' ||
            e.key === 'Home' ||
            e.key === 'End'
          ) {
            e.stopPropagation()
          }
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t('dialogTitle')}</DialogTitle>
          <DialogDescription>{t('dialogDescription')}</DialogDescription>
        </DialogHeader>
        <Command
          loop
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput
            placeholder={t('placeholder')}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="scrollbar-soft">
            {!hasAnyResults && !isSearching && !isPendingSearch && (
              <CommandEmpty>
                <div className="flex flex-col items-center gap-2 py-2">
                  <IconSearchOff className="text-muted-foreground h-10 w-10" />
                  <span>{t('noResults')}</span>
                </div>
              </CommandEmpty>
            )}

            {filteredActions.length > 0 && (
              <CommandGroup heading={t('groups.actions')}>
                {filteredActions.map((item) => (
                  <CommandItem
                    key={item.path}
                    value={item.path}
                    onSelect={() => handleNavigate(item.path)}
                  >
                    <item.icon />
                    <span>{t(item.translationKey)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {filteredPages.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={t('groups.pages')}>
                  {filteredPages.map((item) => (
                    <CommandItem
                      key={item.path}
                      value={item.path}
                      onSelect={() => handleNavigate(item.path)}
                    >
                      <item.icon />
                      {(() => {
                        const text = t(item.translationKey)
                        const segments = text.split(' > ')
                        if (segments.length === 1) {
                          return <span>{text}</span>
                        }
                        return (
                          <span className="flex items-center gap-1">
                            {segments.map((segment, i) => (
                              <React.Fragment key={i}>
                                {i > 0 && (
                                  <IconChevronRight className="text-muted-foreground size-3 shrink-0" />
                                )}
                                <span
                                  className={
                                    i < segments.length - 1
                                      ? 'text-muted-foreground'
                                      : undefined
                                  }
                                >
                                  {segment}
                                </span>
                              </React.Fragment>
                            ))}
                          </span>
                        )
                      })()}
                      {item.shortcutKey && !isMobile && (
                        <CommandShortcut>
                          {formatShortcut(item.shortcutKey)}
                        </CommandShortcut>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {(isSearching || isPendingSearch) && <SearchDialogSkeleton />}

            {searchResults?.envelopes && searchResults.envelopes.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={t('groups.envelopes')}>
                  {searchResults.envelopes.map((item) => (
                    <CommandItem
                      key={item.envelopeId}
                      value={`envelope-${item.envelopeId}`}
                      onSelect={() =>
                        handleNavigate(`/envelopes/${item.envelopeId}`)
                      }
                    >
                      <IconMail />
                      <div className="flex flex-col">
                        <span>{item.envelopeName}</span>
                        {item.vaultName && (
                          <span className="text-muted-foreground text-xs">
                            {item.vaultName}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {searchResults?.documents && searchResults.documents.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={t('groups.documents')}>
                  {searchResults.documents.map((item) => (
                    <CommandItem
                      key={item.documentId}
                      value={`document-${item.documentId}`}
                      onSelect={() =>
                        handleNavigate(`/envelopes/${item.envelopeId}`)
                      }
                    >
                      <IconFileDescription />
                      <div className="flex flex-col">
                        <span>{item.documentName}</span>
                        {item.envelopeName && (
                          <span className="text-muted-foreground text-xs">
                            {item.envelopeName}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {searchResults?.templates && searchResults.templates.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={t('groups.templates')}>
                  {searchResults.templates.map((item) => (
                    <CommandItem
                      key={item.templateId}
                      value={`template-${item.templateId}`}
                      onSelect={() => handleNavigate('/templates')}
                    >
                      <IconFileTextSpark />
                      <div className="flex flex-col">
                        <span>{item.templateName}</span>
                        {item.vaultName && (
                          <span className="text-muted-foreground text-xs">
                            {item.vaultName}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {searchResults?.contacts && searchResults.contacts.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={t('groups.contacts')}>
                  {searchResults.contacts.map((item) => (
                    <CommandItem
                      key={item.contactId}
                      value={`contact-${item.contactId}`}
                      onSelect={() =>
                        handleNavigate(
                          `/contacts?search=${encodeURIComponent(item.contactName ?? '')}`,
                        )
                      }
                    >
                      <Avatar
                        name={item.contactName ?? undefined}
                        className="size-5"
                        fallbackClassName="text-[8px]"
                      />
                      <div className="flex flex-col">
                        <span>{item.contactName}</span>
                        {item.contactEmail && (
                          <span className="text-muted-foreground text-xs">
                            {item.contactEmail}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {searchResults?.signers && searchResults.signers.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={t('groups.signers')}>
                  {searchResults.signers.map((item) => (
                    <CommandItem
                      key={item.signerId}
                      value={`signer-${item.signerId}`}
                      onSelect={() =>
                        handleNavigate(`/envelopes/${item.envelopeId}`)
                      }
                    >
                      <Avatar
                        name={item.signerName ?? item.signerEmail ?? undefined}
                        className="size-5"
                        fallbackClassName="text-[8px]"
                      />
                      <div className="flex flex-col">
                        <span>{item.signerName ?? item.signerEmail}</span>
                        {item.envelopeName && (
                          <span className="text-muted-foreground text-xs">
                            {item.envelopeName}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {searchResults?.vaults && searchResults.vaults.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={t('groups.vaults')}>
                  {searchResults.vaults.map((item) => (
                    <CommandItem
                      key={item.vaultId}
                      value={`vault-${item.vaultId}`}
                      onSelect={() => handleNavigate(`/vaults/${item.vaultId}`)}
                    >
                      <IconShieldLock />
                      <span>{item.vaultName}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {searchResults?.userAccounts &&
              searchResults.userAccounts.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading={t('groups.users')}>
                    {searchResults.userAccounts.map((item) => (
                      <CommandItem
                        key={item.userAccountId}
                        value={`user-${item.userAccountId}`}
                        onSelect={() =>
                          handleNavigate('/settings/account/users')
                        }
                      >
                        <Avatar
                          name={item.userAccountName ?? undefined}
                          src={item.userAccountAvatar}
                          className="size-5"
                          fallbackClassName="text-[8px]"
                        />
                        <div className="flex flex-col">
                          <span>{item.userAccountName}</span>
                          {item.userAccountEmail && (
                            <span className="text-muted-foreground text-xs">
                              {item.userAccountEmail}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
          </CommandList>
          {!isMobile && (
            <div className="-mx-1 -mb-1 border-t px-4 py-3">
              <div className="text-muted-foreground flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1">
                  <Kbd>{formatShortcut('K')}</Kbd>
                  <span>{t('footer.open')}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>↑↓</Kbd>
                  <span>{t('footer.navigate')}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>↵</Kbd>
                  <span>{t('footer.select')}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>esc</Kbd>
                  <span>{t('footer.close')}</span>
                </span>
              </div>
            </div>
          )}
        </Command>
      </DialogContent>
    </Dialog>
  )
}
